import {actions} from "../..";
import {call, put, select, takeEvery, takeLatest} from 'redux-saga/effects';
import {ActionType} from 'typesafe-actions';
import JSONFormData from "../../../utils/JSONFormData";
import {AxiosError, AxiosResponse} from "axios";
import {Voicemail, VoicemailDetails} from "../../actions/payloads";
import {api} from "../../services/axios";
import {Account} from "../../../services/endpoints";
import {showToast} from "../../../utils/showToast";
import i18n from "../../../services/i18n";
import {APIErrorInterface, ReduxState} from "../../types";
import toast from 'react-hot-toast';
import {
    PlayingFile,
    RecordingsPlayingStatus,
    Segment,
    TranscriptionData,
    TranscriptionSegment
} from "../../reducers/calls/reducer";
import dayjs from '../../../services/customDayJs';

export function* getVoicemailList(
    action: ActionType<typeof actions.getVoicemailList.request>,
) {
    try {
        const {session_id, csrf_token} = yield select((state) => state.auth);
        const body = new JSONFormData(session_id, csrf_token);
        const {recordingsPlayer} = yield select((state) => state.calls);

        body.setParams(action.payload);

        const accountDetails: AxiosResponse<{
            messages: Voicemail[];
        }> = yield api.post(Account.GetMailboxMessageList, body);

        const musicFiles = (recordingsPlayer?.filesQueue ?? [])
            .filter((e: PlayingFile[]) => !e.find(c => c.call_recording_id != 'voicemail'));

        for(const mf of accountDetails.data.messages) {
            musicFiles.push([{
                call_recording_id: 'voicemail',
                i_xdr: mf.message_uid,
                h323_conf_id: 'voicemail',
                customDownloadFileAction: () => {;}
            }]);
        }

        yield put(
            actions.updateRecordingsPlayingState({
                ...recordingsPlayer, 
                filesQueue: musicFiles,
                currentFile: null,
                currentBlob: null,
                status: RecordingsPlayingStatus.notDefined
            }),
        );

        yield put(
            actions.getVoicemailList.success({
                voicemails: accountDetails.data.messages,
                i_account: action.payload.i_account
            }),
        );
    } catch (e) {
        //@ts-ignore
        yield put(actions.getVoicemailList.failure(e.response.data));
    }
}

export function* getVoicemailFile(
    action: ActionType<typeof actions.getVoicemailFile.request>,
) {
    try {
        const {session_id, csrf_token} = yield select((state: ReduxState) => state.auth);
        const body = new JSONFormData(session_id, csrf_token);

        body.setParams({...action.payload});
        const res: AxiosResponse<Blob> = yield api.post(
            Account.GetMailboxMessageAttachment,
            body,
            {responseType: 'blob'},
        );

        if(res.data.type == 'audio/wav' || res.data.type == 'image/tiff' || res.data.type == 'application/pdf'
            || res.data.type == 'image/xpng' || res.data.type == 'image/png') {
            const blob = res.data as Blob;
            const objectUrl = URL.createObjectURL(blob);

            yield call(setMailboxMessagesFlag, actions.setMailboxMessagesFlag.request({
                action: "set_flag",
                flag: 'Seen',
                i_account: action.payload.i_account,
                message_uids: [action.payload.message_uid]
            }));

            action.payload.callback && action.payload.callback(objectUrl);
            yield put(actions.getVoicemailFile.success());
        } else {
            showToast(i18n.t<string>('errors:common.noPermissionsToData'));
            action.payload.restrictedCallback && action.payload.restrictedCallback();
            yield put(actions.getVoicemailFile.failure());
        }
    } catch (e) {
        if ((e as AxiosError)?.isAxiosError) {
            const data = (e as AxiosError)?.response?.data;
            if (data) {
                const isJsonBlob = data instanceof Blob && data.type === "application/json";
                //@ts-ignore
                const responseData = isJsonBlob ? (yield data?.text()) : data || {};
                const responseJson = (typeof responseData === "string") ? JSON.parse(responseData) : responseData;
                const err = responseJson as APIErrorInterface;
                if (err?.faultcode?.endsWith('.access_denied')) {
                    toast.dismiss();
                    showToast(i18n.t<string>('errors:common.noPermissionsToData'));
                }
            }
            action.payload.restrictedCallback && action.payload.restrictedCallback();
        }
        yield put(actions.getVoicemailFile.failure());
    }
}

export function* deleteVoicemail(
    action: ActionType<typeof actions.deleteVoicemail.request>,
) {
    try {
        const {session_id, csrf_token} = yield select((state: ReduxState) => state.auth);
        const body = new JSONFormData(session_id, csrf_token);

        body.setParams({...action.payload});
        yield api.post(
            Account.DeleteMailboxMessage,
            body,
        );

        action.payload.callback?.();
        yield put(actions.deleteVoicemail.success());
    } catch (e) {
        yield put(actions.deleteVoicemail.failure());
    }
}

export function* setMailboxMessagesFlag(
    action: ActionType<typeof actions.setMailboxMessagesFlag.request>,
) {
    try {
        const {session_id, csrf_token} = yield select((state: ReduxState) => state.auth);
        const body = new JSONFormData(session_id, csrf_token);

        body.setParams({...action.payload});
        yield api.post(
            Account.SetMailboxMessagesFlag,
            body,
        );

        yield put(actions.setMailboxMessagesFlag.success(action.payload));
    } catch (e) {
        yield put(actions.setMailboxMessagesFlag.failure());
    }
}

export function* getVoicemailDetails(
    action: ActionType<typeof actions.getVoicemailDetails.request>,
) {
    try {
        const {session_id, csrf_token} = yield select((state: ReduxState) => state.auth);
        const body = new JSONFormData(session_id, csrf_token);
        const {internalRecordingsPlayer} = yield select((state) => state.calls);

        body.setParams({...action.payload});
        const res: AxiosResponse<VoicemailDetails> = yield api.post(
            Account.GetVoicemailDetails,
            body,
        );

        let transcription = res.data.body_structures.find(e => e.bodytype == 'text' && e.bodysubtype == 'plain')?.text;

        if(transcription?.length) {
            const match = transcription.match(/Below is the transcribed message:\s+([\s\S]*?)\n-+/);
            transcription = match ? match[1].trim() : undefined;
        }

        const transcriptionExist = (transcription?.length ?? 0) > 0;

        yield put(actions.checkIfTranscriptionExist.success({
            transcriptionExist: transcriptionExist,
        }));

        const duration = dayjs.duration(res.data.voicemail_duration ?? 0, 'seconds');
        const formatted = duration.format('mm:ss');
        
        const transcriptionObject: TranscriptionData = {
            call_recording_id: 'voicemail',
            segments: transcriptionExist
                ? [{
                    segment: {
                        duration: formatted,
                        intervals: {start: "00:00", end: formatted},
                        text: transcription
                    } as TranscriptionSegment,
                    segment_id: 0,
                    speaker: 1,
                } as Segment]
                : []
        } as TranscriptionData;
        yield put(actions.fetchTranscription.success(transcriptionObject));
        
        const file: PlayingFile = {
            call_recording_id: 'voicemail',
            i_xdr: res.data.message_uid,
            h323_conf_id: 'voicemail',
            customDownloadFileAction: () => {;}
        };

        yield put(
            actions.internalUpdateRecordingsPlayingState({
                ...internalRecordingsPlayer, 
                filesQueue: [[file]],
                currentFile: file,
                currentBlob: null,
                status: RecordingsPlayingStatus.notDefined
            }),
        );
        
        action.payload.callback?.(res.data);
        yield put(actions.getVoicemailDetails.success(res.data));
    } catch (e) {
        yield put(actions.getVoicemailDetails.failure());
    }
}

export const voicemailsSaga = [
    takeEvery(actions.getVoicemailList.request, getVoicemailList),
    takeLatest(actions.getVoicemailFile.request, getVoicemailFile),
    takeLatest(actions.deleteVoicemail.request, deleteVoicemail),
    takeLatest(actions.setMailboxMessagesFlag.request, setMailboxMessagesFlag),
    takeLatest(actions.getVoicemailDetails.request, getVoicemailDetails),
];